import type { PollLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  PollPayload,
  PollRuntime,
  PollSlide,
  PollWordCloudSlide,
  WordCloudGroupedView,
  WordCloudParticipantGroup,
} from '../../core/types/payloads/poll.types';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

function emptyAggregates() {
  return { bySlide: {} };
}

function validateSlide(raw: unknown, index: number): PollSlide {
  if (!raw || typeof raw !== 'object') throw new Error(`Slide ${index} invalid`);
  const s = raw as Record<string, unknown>;
  const kind = String(s.kind || 'multiple-choice');
  const base = { id: String(s.id || `slide_${index + 1}`), prompt: String(s.prompt || '') };

  if (kind === 'word-cloud') {
    return { ...base, kind: 'word-cloud', maxWordsPerStudent: Number(s.maxWordsPerStudent) || 3 };
  }
  if (kind === 'scale') {
    return {
      ...base,
      kind: 'scale',
      min: Number(s.min ?? 1),
      max: Number(s.max ?? 5),
      minLabel: s.minLabel ? String(s.minLabel) : undefined,
      maxLabel: s.maxLabel ? String(s.maxLabel) : undefined,
    };
  }
  if (kind === 'slider') {
    return {
      ...base,
      kind: 'slider',
      min: Number(s.min ?? 0),
      max: Number(s.max ?? 100),
      step: Number(s.step ?? 1),
    };
  }
  return {
    ...base,
    kind: 'multiple-choice',
    options: Array.isArray(s.options) ? s.options.map(String) : ['Sí', 'No'],
    allowMultiple: !!s.allowMultiple,
  };
}

export function validatePollPayload(raw: unknown): PollPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Poll payload must be an object');
  const data = raw as Partial<PollPayload>;
  if (!data.title) throw new Error('Poll requires title');
  if (!Array.isArray(data.slides) || data.slides.length === 0) {
    throw new Error('Poll requires at least one slide');
  }
  return {
    title: String(data.title),
    slides: data.slides.map(validateSlide),
    settings: {
      anonymous: data.settings?.anonymous !== false,
      showLiveResults: data.settings?.showLiveResults !== false,
    },
  };
}

export function createInitialPollRuntime(_payload: PollPayload): PollRuntime {
  return {
    currentSlideIndex: 0,
    responses: {},
    aggregates: emptyAggregates(),
  };
}

function normalizePollResponse(slide: PollSlide, data: unknown): unknown | null {
  if (slide.kind === 'multiple-choice') {
    const idx = Number(data);
    if (!Number.isInteger(idx) || idx < 0 || idx >= slide.options.length) return null;
    return idx;
  }

  if (slide.kind === 'word-cloud') {
    const max = slide.maxWordsPerStudent ?? 3;
    let words: string[];
    if (Array.isArray(data)) {
      words = data.map(String).map((w) => w.trim()).filter(Boolean);
    } else if (typeof data === 'string') {
      words = data.split(/[,;\n]+/).map((w) => w.trim()).filter(Boolean);
    } else {
      return null;
    }
    if (words.length === 0) return null;
    return words.slice(0, max);
  }

  if (slide.kind === 'scale' || slide.kind === 'slider') {
    const v = Number(data);
    if (!Number.isFinite(v) || v < slide.min || v > slide.max) return null;
    if (slide.kind === 'slider' && slide.step && slide.step > 0) {
      const steps = Math.round((v - slide.min) / slide.step);
      return slide.min + steps * slide.step;
    }
    return v;
  }

  return null;
}

function collectWordCloudCounts(
  slideId: string,
  responses: PollRuntime['responses'],
): Record<string, number> {
  const words: Record<string, number> = {};
  for (const participantResponses of Object.values(responses)) {
    const raw = participantResponses[slideId];
    const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
    for (const word of list) {
      const key = String(word).trim().toLowerCase();
      if (key) words[key] = (words[key] || 0) + 1;
    }
  }
  return words;
}

/** Resuelve palabras para la nube: agregados del servidor o cálculo local desde responses. */
export function resolveWordCloudWords(
  slide: PollWordCloudSlide,
  runtime: PollRuntime,
  slideIndex = 0,
): Record<string, number> {
  const candidateIds = [
    slide.id,
    slide.id ? undefined : `slide_${slideIndex + 1}`,
  ].filter(Boolean) as string[];

  for (const slideId of candidateIds) {
    const agg = runtime.aggregates?.bySlide?.[slideId];
    if (agg?.kind === 'word-cloud' && Object.keys(agg.words).length > 0) {
      return agg.words;
    }
    const fromResponses = collectWordCloudCounts(slideId, runtime.responses ?? {});
    if (Object.keys(fromResponses).length > 0) {
      return fromResponses;
    }
  }

  const bySlide = runtime.aggregates?.bySlide ?? {};
  for (const agg of Object.values(bySlide)) {
    if (agg.kind === 'word-cloud' && Object.keys(agg.words).length > 0) {
      return agg.words;
    }
  }

  return {};
}

const GROUP_ACCENTS = [
  '#6366f1',
  '#e879f9',
  '#22d3ee',
  '#fbbf24',
  '#34d399',
  '#fb7185',
  '#a855f7',
  '#ec4899',
];

function slideIdCandidates(slide: PollWordCloudSlide, slideIndex: number): string[] {
  return [slide.id, slide.id ? undefined : `slide_${slideIndex + 1}`].filter(Boolean) as string[];
}

function wordsFromResponse(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).map((w) => w.trim()).filter(Boolean);
  if (typeof raw === 'string') return raw.split(/[,;\n]+/).map((w) => w.trim()).filter(Boolean);
  return [];
}

function pickDisplayForm(lowerKey: string, originals: string[]): string {
  return originals.find((w) => w.toLowerCase() === lowerKey) ?? lowerKey;
}

/** Agrupa respuestas por persona con pesos globales y consenso de clase. */
export function resolveWordCloudGroups(
  slide: PollWordCloudSlide,
  runtime: PollRuntime,
  participants: Participant[],
  options?: {
    slideIndex?: number;
    highlightParticipantId?: string;
    /** Oculta nombres ajenos (vista estudiante anónima) */
    maskOtherNames?: boolean;
  },
): WordCloudGroupedView {
  const slideIds = slideIdCandidates(slide, options?.slideIndex ?? 0);
  const globalCounts: Record<string, number> = {};
  const wordPeople: Record<string, Set<string>> = {};
  const originalForms: string[] = [];

  for (const [participantId, slideResponses] of Object.entries(runtime.responses ?? {})) {
    for (const slideId of slideIds) {
      const words = wordsFromResponse(slideResponses[slideId]);
      if (words.length === 0) continue;
      for (const word of words) {
        const key = word.toLowerCase();
        originalForms.push(word);
        globalCounts[key] = (globalCounts[key] || 0) + 1;
        if (!wordPeople[key]) wordPeople[key] = new Set();
        wordPeople[key].add(participantId);
      }
      break;
    }
  }

  const globalMax = Math.max(1, ...Object.values(globalCounts));
  const participantMap = new Map(participants.map((p) => [p.participantId, p]));

  const responseEntries = Object.entries(runtime.responses ?? {})
    .map(([participantId, slideResponses]) => {
      for (const slideId of slideIds) {
        const words = wordsFromResponse(slideResponses[slideId]);
        if (words.length > 0) return { participantId, words };
      }
      return null;
    })
    .filter(Boolean) as Array<{ participantId: string; words: string[] }>;

  responseEntries.sort((a, b) => {
    const hi = options?.highlightParticipantId;
    if (hi) {
      if (a.participantId === hi) return -1;
      if (b.participantId === hi) return 1;
    }
    const nameA = participantMap.get(a.participantId)?.displayName ?? '';
    const nameB = participantMap.get(b.participantId)?.displayName ?? '';
    return b.words.length - a.words.length || nameA.localeCompare(nameB);
  });

  const groups: WordCloudParticipantGroup[] = responseEntries.map(({ participantId, words }, index) => {
    const isSelf = participantId === options?.highlightParticipantId;
    const p = participantMap.get(participantId);
    const label =
      options?.maskOtherNames && !isSelf
        ? `Respuesta ${index + 1}`
        : (p?.displayName ?? `Alumno ${index + 1}`);

    const seen = new Set<string>();
    const uniqueWords = words.filter((w) => {
      const k = w.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    return {
      participantId,
      label,
      accent: GROUP_ACCENTS[index % GROUP_ACCENTS.length],
      words: uniqueWords.map((text, rankInResponse) => ({
        text,
        globalCount: globalCounts[text.toLowerCase()] ?? 1,
        rankInResponse,
      })),
    };
  });

  const consensus = Object.entries(globalCounts)
    .filter(([key]) => (wordPeople[key]?.size ?? 0) >= 2)
    .map(([key, globalCount]) => ({
      text: pickDisplayForm(key, originalForms),
      globalCount,
      peopleCount: wordPeople[key]?.size ?? 0,
    }))
    .sort((a, b) => b.peopleCount - a.peopleCount || b.globalCount - a.globalCount);

  const totalMentions = Object.values(globalCounts).reduce((sum, c) => sum + c, 0);

  return {
    groups,
    consensus,
    totalPeople: groups.length,
    totalMentions,
    globalMax,
  };
}

function rebuildAggregates(payload: PollPayload, responses: PollRuntime['responses']): PollRuntime['aggregates'] {
  const bySlide: PollRuntime['aggregates']['bySlide'] = {};

  for (const slide of payload.slides) {
    if (slide.kind === 'word-cloud') {
      const words = collectWordCloudCounts(slide.id, responses);
      bySlide[slide.id] = { kind: 'word-cloud', words };
    } else if (slide.kind === 'multiple-choice') {
      const counts = slide.options.map(() => 0);
      for (const participantResponses of Object.values(responses)) {
        const idx = participantResponses[slide.id];
        if (typeof idx === 'number' && idx >= 0 && idx < counts.length) {
          counts[idx] += 1;
        }
      }
      bySlide[slide.id] = { kind: 'multiple-choice', counts };
    } else if (slide.kind === 'scale' || slide.kind === 'slider') {
      const values: number[] = [];
      for (const participantResponses of Object.values(responses)) {
        const v = Number(participantResponses[slide.id]);
        if (Number.isFinite(v)) values.push(v);
      }
      const average = values.length
        ? values.reduce((a, b) => a + b, 0) / values.length
        : 0;
      bySlide[slide.id] = { kind: slide.kind, values, average };
    }
  }

  return { bySlide };
}

export const pollLogic: PollLogicPlugin = {
  mode: 'poll',

  validatePayload: validatePollPayload,
  createInitialRuntime: createInitialPollRuntime,

  aggregate(ctx) {
    return rebuildAggregates(ctx.payload, ctx.runtime.responses);
  },

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (input.action !== 'RESPOND') {
      return { runtime: ctx.runtime };
    }

    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }

    const slide = ctx.payload.slides[ctx.runtime.currentSlideIndex];
    if (!slide) return { runtime: ctx.runtime };

    const responses = { ...ctx.runtime.responses };
    const participantResponses = { ...(responses[participant.participantId] || {}) };

    if (participantResponses[slide.id] !== undefined) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'ALREADY_ANSWERED' };
    }

    const normalized = normalizePollResponse(slide, input.data);
    if (normalized === null) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_RESPONSE' };
    }

    participantResponses[slide.id] = normalized;
    responses[participant.participantId] = participantResponses;

    const aggregates = rebuildAggregates(ctx.payload, responses);
    return {
      runtime: {
        ...ctx.runtime,
        responses,
        aggregates,
      },
    };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'NEXT_SLIDE') {
      const next = Math.min(
        ctx.runtime.currentSlideIndex + 1,
        ctx.payload.slides.length - 1,
      );
      return {
        runtime: {
          ...ctx.runtime,
          currentSlideIndex: next,
          slideStartedAt: Date.now(),
        },
      };
    }
    if (action.action === 'PREV_SLIDE') {
      const prev = Math.max(ctx.runtime.currentSlideIndex - 1, 0);
      return { runtime: { ...ctx.runtime, currentSlideIndex: prev } };
    }
    return { runtime: ctx.runtime };
  },
};

export function demoPollPayload(): PollPayload {
  return {
    title: 'Ticket de entrada',
    slides: [
      {
        id: 's1',
        kind: 'multiple-choice',
        prompt: '¿Qué esperas aprender hoy?',
        options: ['Conceptos nuevos', 'Repasar', 'Practicar', 'No sé aún'],
      },
      {
        id: 's2',
        kind: 'scale',
        prompt: '¿Qué tan familiar te sientes con el tema?',
        min: 1,
        max: 5,
        minLabel: 'Nada',
        maxLabel: 'Mucho',
      },
    ],
  };
}
